import moment from 'moment'
import { capitalizeFirstLetter, nonBreakingSpace } from '../util/helpers'
import { Countries } from './countries'
import { ChatLine } from './chat'

export const countrySupportInfo = {
  SE: {
    CreditSupport: {
      phoneNumber: '042 - 38 20 94',
      openingHours: [
        {
          from: '7.15',
          to: '20.15',
          days: [1, 2, 3, 4, 5],
        },
        {
          from: '9.00',
          to: '18.00',
          days: [6, 7],
        },
      ],
    },
    CustomerSupport: {
      phoneNumber: '0771 - 11 22 33',
      openingHours: [
        {
          from: '9.00',
          to: '17.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
    ChatSupport: {
      openingHours: [
        {
          from: '8.00',
          to: '17.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
  },
  NO: {
    CreditSupport: {
      phoneNumber: '22 56 37 35',
      openingHours: [
        {
          from: '8.00',
          to: '20.00',
          days: [1, 2, 3, 4, 5],
        },
        {
          from: '9.00',
          to: '18.00',
          days: [6, 7],
        },
      ],
    },
    CustomerSupport: {
      phoneNumber: '22 56 37 33',
      openingHours: [
        {
          from: '8.00',
          to: '17.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
    ChatSupport: {
      openingHours: [
        {
          from: '8.00',
          to: '17.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
  },
  DK: {
    CreditSupport: {
      phoneNumber: '39 13 16 60',
      openingHours: [
        {
          from: '9.00',
          to: '18.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
    CustomerSupport: {
      phoneNumber: '39 13 16 60',
      openingHours: [
        {
          from: '9.00',
          to: '20.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
    ChatSupport: {
      openingHours: [
        {
          from: '8.00',
          to: '17.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
  },
  FI: {
    CreditSupport: {
      phoneNumber: '09 622 4309',
      openingHours: [
        {
          from: '8.00',
          to: '18.00',
          days: [1, 2, 3, 4, 5],
        },
        {
          from: '11.00',
          to: '18.00',
          days: [6],
        },
        {
          from: '14.00',
          to: '18.00',
          days: [7],
        },
      ],
    },
    CustomerSupport: {
      phoneNumber: '09 - 622 4308',
      openingHours: [
        {
          from: '8.00',
          to: '18.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
    ChatSupport: {
      openingHours: [
        {
          from: '9.00',
          to: '18.00',
          days: [1, 2, 3, 4, 5],
        },
      ],
    },
  },
}

const formatDayOfWeek = dayOfWeek =>
  moment().isoWeekday(dayOfWeek).format('ddd')

const formatOpeningHoursForWeekday = (dayOfWeek, hoursOfDay) =>
  `${formatDayOfWeek(dayOfWeek)} ${hoursOfDay}`

const formatOpeningHoursForInterval = (start, end, hoursOfDay) =>
  `${formatDayOfWeek(start)}–${formatDayOfWeek(end)} ${hoursOfDay}`

const beautifyContactInformation = ({ phoneNumber, openingHours }) => {
  const hoursOfDay = {}
  openingHours.forEach(({ from, to, days }) => {
    days.forEach(dayOfWeek => {
      hoursOfDay[dayOfWeek] = `${from}–${to}`
    })
  })
  const lines = []
  let currentInterval = 0
  let intervalStart = 1
  ;[1, 2, 3, 4, 5, 6, 7].forEach(dayOfWeek => {
    if (hoursOfDay[dayOfWeek] !== hoursOfDay[intervalStart]) {
      currentInterval += 1
      intervalStart = dayOfWeek
    }

    if (!hoursOfDay[intervalStart]) {
      // not open
      lines[currentInterval] = null
    } else if (dayOfWeek === intervalStart) {
      // one day
      lines[currentInterval] = formatOpeningHoursForWeekday(
        dayOfWeek,
        hoursOfDay[dayOfWeek],
      )
    } else {
      // interval
      lines[currentInterval] = formatOpeningHoursForInterval(
        intervalStart,
        dayOfWeek,
        hoursOfDay[intervalStart],
      )
    }
  })
  return {
    phoneNumber: nonBreakingSpace(phoneNumber),
    openingHours: capitalizeFirstLetter(lines.filter(dayHours => !!dayHours)),
  }
}

const prefixSEPhoneNumber = ({ phoneNumber, ...data }) => ({
  ...data,
  phoneNumber: phoneNumber.replace(/^(0)(.*)$/g, '+46 (0) $2'),
})

export const getErrandTypes = (
  storeLabel,
  terminalLabel,
  materialLabel,
  paymentsLabel,
  creditLabel,
) => {
  return [
    {
      SE: {
        title: storeLabel,
        value: '726',
        allowedRoles: '*',
        defaultFor: 'transactions, support',
        type: ChatLine.STORE_SUPPORT,
      },
      DK: {
        title: storeLabel,
        value: '736',
        allowedRoles: '*',
        defaultFor: 'transactions, support',
        type: ChatLine.STORE_SUPPORT,
      },
      NO: {
        title: storeLabel,
        value: '741',
        allowedRoles: '*',
        defaultFor: 'transactions, support',
        type: ChatLine.STORE_SUPPORT,
      },
      FI: {
        title: storeLabel,
        value: '731',
        allowedRoles: '*',
        defaultFor: 'transactions, support',
        type: ChatLine.STORE_SUPPORT,
      },
    },
    {
      SE: {
        title: terminalLabel,
        value: '727',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.TERMINAL_SUPPORT,
      },
      DK: {
        title: terminalLabel,
        value: '737',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.TERMINAL_SUPPORT,
      },
      NO: {
        title: terminalLabel,
        value: '742',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.TERMINAL_SUPPORT,
      },
    },
    {
      SE: {
        title: materialLabel,
        value: '728',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.STORE_MATERIAL_SUPPORT,
      },
      DK: {
        title: materialLabel,
        value: '738',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.STORE_MATERIAL_SUPPORT,
      },
      NO: {
        title: materialLabel,
        value: '743',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.STORE_MATERIAL_SUPPORT,
      },
      FI: {
        title: materialLabel,
        value: '733',
        allowedRoles: '*',
        defaultFor: '',
        type: ChatLine.STORE_MATERIAL_SUPPORT,
      },
    },
    {
      SE: {
        title: paymentsLabel,
        value: '729',
        allowedRoles: 'FINANCE',
        defaultFor: 'finance',
        type: ChatLine.PAYMENTS_SUPPORT,
      },
      DK: {
        title: paymentsLabel,
        value: '739',
        allowedRoles: 'FINANCE',
        defaultFor: 'finance',
        type: ChatLine.PAYMENTS_SUPPORT,
      },
      NO: {
        title: paymentsLabel,
        value: '744',
        allowedRoles: 'FINANCE',
        defaultFor: 'finance',
        type: ChatLine.PAYMENTS_SUPPORT,
      },
      FI: {
        title: paymentsLabel,
        value: '734',
        allowedRoles: 'FINANCE',
        defaultFor: 'finance',
        type: ChatLine.PAYMENTS_SUPPORT,
      },
    },
    {
      SE: {
        title: creditLabel,
        value: '730',
        allowedRoles: '*',
        defaultFor: 'apply',
        contactData: [
          {
            key: 'customerId',
          },
          {
            key: 'mupId',
          },
        ],
        type: ChatLine.CREDIT_SUPPORT,
      },
      DK: {
        title: creditLabel,
        value: '740',
        allowedRoles: '*',
        defaultFor: 'apply',
        contactData: [
          {
            key: 'customerId',
          },
          {
            key: 'mupId',
          },
        ],
        type: ChatLine.CREDIT_SUPPORT,
      },
      NO: {
        title: creditLabel,
        value: '745',
        allowedRoles: '*',
        defaultFor: 'apply',
        contactData: [
          {
            key: 'customerId',
          },
          {
            key: 'mupId',
          },
        ],
        type: ChatLine.CREDIT_SUPPORT,
      },
      FI: {
        title: creditLabel,
        value: '735',
        allowedRoles: '*',
        defaultFor: 'apply',
        contactData: [
          {
            key: 'customerId',
          },
          {
            key: 'mupId',
          },
        ],
        type: ChatLine.CREDIT_SUPPORT,
      },
    },
  ]
}

export const chatEntrance = {
  SE: 'SE_Merchant_Portal',
  DK: 'DK_Merchant_Portal',
  NO: 'NO_Merchant_Portal',
  FI: 'FI_Merchant_Portal',
}

export const getLocalizedCustomerSupport = countryCode => {
  switch (countryCode) {
    case Countries.SE:
      return beautifyContactInformation(countrySupportInfo.SE.CustomerSupport)
    case Countries.DK:
      return beautifyContactInformation(countrySupportInfo.DK.CustomerSupport)
    case Countries.FI:
      return beautifyContactInformation(countrySupportInfo.FI.CustomerSupport)
    case Countries.NO:
      return beautifyContactInformation(countrySupportInfo.NO.CustomerSupport)
    default:
      return prefixSEPhoneNumber(
        beautifyContactInformation(countrySupportInfo.SE.CustomerSupport),
      )
  }
}

export const getLocalizedChatSupport = countryCode => {
  switch (countryCode) {
    case Countries.SE:
      return beautifyContactInformation(countrySupportInfo.SE.ChatSupport)
    case Countries.DK:
      return beautifyContactInformation(countrySupportInfo.DK.ChatSupport)
    case Countries.FI:
      return beautifyContactInformation(countrySupportInfo.FI.ChatSupport)
    case Countries.NO:
      return beautifyContactInformation(countrySupportInfo.NO.ChatSupport)
    default:
      return prefixSEPhoneNumber(
        beautifyContactInformation(countrySupportInfo.SE.ChatSupport),
      )
  }
}

export const getChatSupportOpeningHours = (countryCode, chatType) => {
  const unknown = {
    openingHours: [
      {
        from: '0.00',
        to: '0.00',
        days: [1, 2, 3, 4, 5, 6, 7],
      },
    ],
  }
  if (countryCode && chatType) {
    return countrySupportInfo[countryCode][chatType] || unknown
  }
  return unknown
}

export const isSelectedChatSupportOpen = (countryCode, chatType) => {
  const currentDayOfWeek = moment().isoWeekday()
  const _openingHours = getChatSupportOpeningHours(
    countryCode,
    chatType,
  ).openingHours.filter(obj => obj.days.includes(currentDayOfWeek))

  if (!_openingHours) {
    return false
  }

  return _openingHours.some(({ from, to }) => {
    return moment().isBetween(moment(from, 'HH.mm'), moment(to, 'HH.mm'))
  })
}

export const getLocalizedCreditSupport = countryCode => {
  return beautifyContactInformation(
    getChatSupportOpeningHours(countryCode, ChatLine.CREDIT_SUPPORT),
  )
}
